/**
 * SplatViewer with original XR service for testing
 */

import { useEffect, useRef, useState } from 'react'
import { SplatRenderer } from './splatv-renderer'
import { useXR } from '@/hooks/useXR'
import type { CameraState } from '@/types/camera'

interface SplatViewerXROriginalProps {
  modelUrl: string
  defaultCamera?: CameraState
  onLoad?: () => void
  onError?: (error: Error) => void
}

export function SplatViewerXROriginal({
  modelUrl,
  defaultCamera,
  onLoad,
  onError
}: SplatViewerXROriginalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rendererRef = useRef<SplatRenderer | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const xr = useXR({
    enableFallback: false, // Only use WebXR for original service
    onSessionStart: () => {
      console.log('XR session (original) started')
    },
    onSessionEnd: () => {
      console.log('XR session (original) ended')
    }
  })
  
  // Initialize renderer
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const renderer = new SplatRenderer(canvas, {
      controls: true,
      quality: 'high',
      defaultCamera
    })
    
    rendererRef.current = renderer
    
    // Load model
    renderer.loadModel(modelUrl)
    
    // Handle events
    renderer.on('load', () => {
      console.log('Model loaded successfully')
      setIsLoading(false)
      
      // Original XR service doesn't need setRenderer
      // It creates its own rendering pipeline
      console.log('Using original XR service - will create its own renderer')
      
      onLoad?.()
    })
    
    renderer.on('error', (err: any) => {
      setIsLoading(false)
      const errorMessage = err?.message || 'Unknown error'
      setError(errorMessage)
      if (onError && err instanceof Error) {
        onError(err)
      }
    })
    
    // Check XR support
    console.log('Checking XR support with original service...')
    xr.checkSupport()
    
    return () => {
      renderer.dispose()
    }
  }, [modelUrl])
  
  // Debug XR state
  useEffect(() => {
    console.log('XR State (original):', xr.state)
  }, [xr.state])
  
  const handleEnterAR = () => {
    console.log('handleEnterAR clicked (original service)')
    if (canvasRef.current) {
      // Original XR service loads the model URL directly
      xr.enterAR(canvasRef.current, modelUrl)
    }
  }
  
  const handleEnterVR = () => {
    console.log('handleEnterVR clicked (original service)')
    if (canvasRef.current) {
      // Original XR service loads the model URL directly
      xr.enterVR(canvasRef.current, modelUrl)
    }
  }
  
  // const handleExitXR = () => {
  //   console.log('handleExitXR clicked')
  //   xr.exitXR()
  // }
  
  return (
    <div className="relative w-full h-full bg-gray-900">
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ touchAction: 'none' }}
      />
      
      {/* Test Button with inline styles - should ALWAYS be visible */}
      <div style={{ 
        position: 'fixed', 
        bottom: '20px', 
        left: '20px', 
        zIndex: 99999,
        pointerEvents: 'all'
      }}>
        <button 
          onClick={() => {
            alert('Button clicked! Trying AR with ORIGINAL service...')
            handleEnterAR()
          }}
          style={{
            padding: '20px',
            fontSize: '20px',
            backgroundColor: '#ff0000',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            pointerEvents: 'all'
          }}
        >
          ORIGINAL XR - AR TEST
        </button>
      </div>
      
      {/* Simple XR buttons with inline styles - ALWAYS VISIBLE */}
      <div style={{ 
        position: 'fixed', 
        bottom: '20px', 
        right: '20px', 
        zIndex: 99999,
        display: 'flex',
        gap: '10px',
        pointerEvents: 'all'
      }}>
        <button 
          onClick={() => {
            alert('AR button clicked (ORIGINAL)!')
            handleEnterAR()
          }}
          style={{
            padding: '15px 30px',
            fontSize: '18px',
            backgroundColor: xr.state.arModeAvailable ? '#9333ea' : '#666666',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            pointerEvents: 'all'
          }}
        >
          Enter AR (Original) {xr.state.arModeAvailable ? '✓' : '✗'}
        </button>
        
        <button 
          onClick={() => {
            alert('VR button clicked (ORIGINAL)!')
            handleEnterVR()
          }}
          style={{
            padding: '15px 30px',
            fontSize: '18px',
            backgroundColor: xr.state.vrModeAvailable ? '#4f46e5' : '#666666',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            pointerEvents: 'all'
          }}
        >
          Enter VR (Original) {xr.state.vrModeAvailable ? '✓' : '✗'}
        </button>
      </div>
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-white">Loading model...</div>
        </div>
      )}
      
      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-red-500">Error: {error}</div>
        </div>
      )}
      
      {/* Debug Info with inline styles */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: 9998
      }}>
        <div style={{color: 'yellow'}}>USING ORIGINAL XR SERVICE</div>
        <div>Loading: {isLoading ? 'YES' : 'NO'}</div>
        <div>Error: {error || 'none'}</div>
        <div>XR Supported: {xr.state.isSupported ? 'YES' : 'NO'}</div>
        <div>AR Available: {xr.state.arModeAvailable || 'NO'}</div>
        <div>VR Available: {xr.state.vrModeAvailable || 'NO'}</div>
        <div>XR Active: {xr.state.isActive ? 'YES' : 'NO'}</div>
        <div>Session Type: {xr.state.sessionType || 'none'}</div>
      </div>
    </div>
  )
}